// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import googleAuthService from '../services/googleAuth';
import authService from '../services/authService';
import userService from '../services/userService';


const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [hasToken, setHasToken] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    if (localStorage.getItem('authToken')) {
      setHasToken(true);
    }

    const initAuth = async () => {
      try {
        /*const initialized = await googleAuthService.init();
        if (!initialized) {
          throw new Error('Failed to initialize Google Auth Service');
        }
        */

        // Check if we have a token and fetch user data
        if (localStorage.getItem('authToken')) {
          await fetchUserData();
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setInitError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    // Reset signing in state when page is about to unload
    const handleBeforeUnload = () => {
      setIsSigningIn(false);
    };

    // Reset signing in state when page becomes visible again (user switched back from popup)
    const handleVisibilityChange = () => {
      if (!document.hidden && isSigningIn) {
        console.log('👁️ Page became visible and isSigningIn is true - resetting state');
        setTimeout(() => {
          setIsSigningIn(false);
        }, 2000); // 2 second delay to allow for auth completion
      }
    };

    // Reset signing in state when window gets focus (user clicked back to main window)
    const handleWindowFocus = () => {
      if (isSigningIn) {
        console.log('🎯 Window focused and isSigningIn is true - checking if we should reset');
        setTimeout(() => {
          if (isSigningIn) {
            console.log('🔄 Resetting isSigningIn state due to window focus');
            setIsSigningIn(false);
          }
        }, 3000); // 3 second delay
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    initAuth();

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  // Safety mechanism: Auto-reset isSigningIn if it stays true for too long
  useEffect(() => {
    if (isSigningIn) {
      console.log('⏱️ Setting safety timeout for isSigningIn state');
      const safetyTimeout = setTimeout(() => {
        console.log('🔄 Safety timeout triggered - resetting isSigningIn to false');
        setIsSigningIn(false);
      }, 10000); // 10 second safety timeout

      return () => {
        clearTimeout(safetyTimeout);
      };
    }
  }, [isSigningIn]);

  const fetchUserData = async () => {
    try {
      const userData = await userService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Clear invalid token
      localStorage.removeItem('authToken');
      setUser(null);
    }
  };

  const signInWithGoogle = async () => {
    if (initError) {
      return {
        success: false,
        error: initError
      };
    }

    // If already signing in, force reset and try again
    if (isSigningIn) {
      console.log('🔄 Already signing in - forcing reset and trying again');
      setIsSigningIn(false);
      // Small delay to let state update
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    console.log('🔄 Starting Google sign-in, setting isSigningIn to true');
    setIsSigningIn(true);

    // Force reset after 15 seconds no matter what
    const forceResetTimeout = setTimeout(() => {
      console.log('🚨 FORCE RESET: Setting isSigningIn to false after 15 seconds');
      setIsSigningIn(false);
    }, 15000);

    try {
      console.log('📞 Calling googleAuthService.signIn()');

      // Wrap the googleAuthService call with our own timeout
      const signInPromise = googleAuthService.signIn();
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('popup_closed'));
        }, 8000); // 8 second timeout
      });

      const result = await Promise.race([signInPromise, timeoutPromise]);
      console.log('📋 Google sign-in result:', result);

      // Check if the sign-in was successful
      if (!result.success) {
        console.log('❌ Google sign-in was not successful');
        clearTimeout(forceResetTimeout);
        setIsSigningIn(false);
        return {
          success: false,
          error: result.error || 'Failed to sign in with Google'
        };
      }

      // Get JWT token after successful Google sign-in
      console.log('🔑 Getting JWT token with auth code');
      const loginResult = await authService.getJWTTokenGoogle(result.code);

      if (loginResult?.user) {
        setUser(loginResult.user);
      }

      clearTimeout(forceResetTimeout);
      setIsSigningIn(false);
      return {
        success: true,
        message: "Successfully signed in with Google"
      }
    } catch (error) {
      console.error('❌ Google sign-in error:', error);
      clearTimeout(forceResetTimeout);

      // IMMEDIATELY reset state with multiple approaches
      setIsSigningIn(false);
      console.log('🔧 IMMEDIATE RESET: isSigningIn set to false');

      // Force another reset after a small delay
      setTimeout(() => {
        setIsSigningIn(false);
        console.log('🔧 DELAYED RESET: isSigningIn set to false again');
      }, 100);

      // Handle specific popup closure error
      if (error.message === 'popup_closed') {
        console.log('🚫 Popup was closed, returning cancellation error');
        return {
          success: false,
          error: 'Authentication cancelled. Please try again.'
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to sign in with Google'
      };
    }
  };

  const signInWithMagicLink = async (email) => {
    setIsSigningIn(true);
    try {
      const result = await authService.requestMagicLogin(email);
      return {
        success: true,
        message: result.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to send magic login link'
      };
    } finally {
      setIsSigningIn(false);
    }
  };

  const verifyMagicLink = async (token) => {
    setIsSigningIn(true);
    try {
      const loginResult = await authService.verifyMagicLoginToken(token);
      console.log({ loginResult })
      if (loginResult?.user) {
        setUser(loginResult.user);
      }
      if (loginResult?.access_token) {
        localStorage.setItem('authToken', loginResult.access_token);
        setHasToken(true);
      }

      return {
        success: true,
        message: "Successfully verified magic login link"
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to verify magic login link'
      };
    } finally {
      setIsSigningIn(false);
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Manual reset function for debugging/recovery
  const resetAuthState = () => {
    console.log('🔧 Manually resetting auth state');
    setIsSigningIn(false);
    setInitError(null);
  };

  // Debug function to check current state
  const debugAuthState = () => {
    console.log('🐛 Current Auth State:', {
      isSigningIn,
      isLoading,
      initError,
      user: user ? 'User exists' : 'No user',
      isAuthenticated: !!user
    });
    return { isSigningIn, isLoading, initError, user: !!user };
  };

  const value = {
    hasToken,
    user,
    isLoading,
    isSigningIn,
    initError,
    signInWithGoogle,
    signInWithMagicLink,
    verifyMagicLink,
    signOut,
    resetAuthState, // Add this for debugging
    debugAuthState, // Add this for debugging
    isAuthenticated: !!user
  };

  // Make debug functions available globally for console access
  if (typeof window !== 'undefined') {
    window.debugGoogleAuth = {
      checkState: debugAuthState,
      forceReset: resetAuthState,
      currentState: () => ({ isSigningIn, isLoading, initError, user: !!user })
    };
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};